import dynamic from 'next/dynamic'
import Head from 'next/head'
import Script from 'next/script'
import { useTranslation } from 'next-i18next'
import { type PropsWithChildren, type ReactNode } from 'react'

import { AuthModal } from '@/components/shared/auth-modal/AuthModal'
import { GlobalHeader } from '@/components/shared/global-header'
import { PSABanner } from '@/components/shared/psa-banner/PSABanner'
import { PSA_BANNER_KEYS } from '@/constants/psaBanner'
import { Flex } from '@/ui/atoms/flex'
import { SkipNavContent, SkipNavLink } from '@/ui/atoms/skip-nav'

import { BaseLayoutBody, BaseLayoutWrapper } from './styled'

const GlobalFooter = dynamic(
  () => import('@/components/shared/global-footer').then((mod) => mod.GlobalFooter),
  { ssr: false }
)

const CURRENT_PSA_CAMPAIGN = PSA_BANNER_KEYS.SITE_MAINTENANCE

export type BaseLayoutProps = PropsWithChildren<{
  /**
   * Action to be triggered when any extra events should be triggered on search submit
   * */
  handleSearchSubmit?: (categoryId: number, keyword: string) => Promise<void>
  /**
   * It will append a component below the SearchBar
   * It is used for the mega-menu on the HP
   */
  searchBarChildren?: ReactNode
  /**
   * Will show the search bar in the header. Defaults to false.
   */
  showSearchBar?: boolean
  /**
   * Render an Auth Modal on the page that can be triggered on
   */
  withAuthModal?: boolean
  /**
   *  If true, the main content will fill the full container.
   */
  isMainFullHeight?: boolean
  /**
   * Include the RUM data tracking scripts to speedcurve and corewebvitals.io
   */
  includeRUMScripts?: boolean
  /**
   * RUM data page label to be used for CoreWebVitals.io
   */
  rumPageLabel?: string
}>

/**
 * Layout defined for general pages with header + content + footer. Must be used on all root pages.
 */

export const BaseLayout = ({
  children,
  handleSearchSubmit,
  isMainFullHeight = false,
  showSearchBar = false,
  withAuthModal = false,
  includeRUMScripts = false,
  rumPageLabel,
  searchBarChildren,
}: BaseLayoutProps) => {
  const { t } = useTranslation('common')
  const rumSampleRate = rumPageLabel === 'Homepage New' ? 0.05 : 0.01

  return (
    <>
      {rumPageLabel && (
        <Head key="rum-page-label">
          <script>{`window.__CWVL='${rumPageLabel}';`}</script>
        </Head>
      )}
      <PSABanner psaKey={CURRENT_PSA_CAMPAIGN} />

      <BaseLayoutWrapper>
        <SkipNavLink contentId="skip-to-main">{t('skip_nav')}</SkipNavLink>

        <BaseLayoutBody>
          <GlobalHeader
            psaCampaign={CURRENT_PSA_CAMPAIGN}
            handleSearchSubmit={handleSearchSubmit}
            showSearchBar={showSearchBar}
          >
            {searchBarChildren}
          </GlobalHeader>

          <SkipNavContent id="skip-to-main" />

          {isMainFullHeight ? (
            <Flex
              flexGrow={1}
              flexBasis="0%"
              flexShrink={1}
              id="base-layout-main-wrapper"
              as="main"
            >
              {children}
            </Flex>
          ) : (
            <main id="base-layout-main-wrapper">{children}</main>
          )}
          {withAuthModal && <AuthModal />}
          <GlobalFooter />
        </BaseLayoutBody>
      </BaseLayoutWrapper>

      {/* START - Temporary speed curve integration scripts to capture RUM (Real User Monitoring data) to be used for CWV audit */}
      {includeRUMScripts && process.env.NODE_ENV === 'production' && (
        <>
          <Script
            id="speedcurve-lux-setup"
            dangerouslySetInnerHTML={{
              __html: `LUX=function(){function n(){return Date.now?Date.now():+new Date}var r,e=n(),t=window.performance||{},a=t.timing||{navigationStart:(null===(r=window.LUX)||void 0===r?void 0:r.ns)||e};function o(){return t.now?(r=t.now(),Math.floor(r)):n()-a.navigationStart;var r}(LUX=window.LUX||{}).ac=[],LUX.addData=function(n,r){return LUX.cmd(["addData",n,r])},LUX.cmd=function(n){return LUX.ac.push(n)},LUX.getDebug=function(){return[[e,0,[]]]},LUX.init=function(){return LUX.cmd(["init"])},LUX.mark=function(){for(var n=[],r=0;r<arguments.length;r++)n[r]=arguments[r];if(t.mark)return t.mark.apply(t,n);var e=n[0],a=n[1]||{};void 0===a.startTime&&(a.startTime=o());LUX.cmd(["mark",e,a])},LUX.markLoadTime=function(){return LUX.cmd(["markLoadTime",o()])},LUX.measure=function(){for(var n=[],r=0;r<arguments.length;r++)n[r]=arguments[r];if(t.measure)return t.measure.apply(t,n);var e,a=n[0],i=n[1],u=n[2];e="object"==typeof i?n[1]:{start:i,end:u};e.duration||e.end||(e.end=o());LUX.cmd(["measure",a,e])},LUX.send=function(){return LUX.cmd(["send"])},LUX.ns=e;var i=LUX;if(window.LUX_ae=[],window.addEventListener("error",(function(n){window.LUX_ae.push(n)})),window.LUX_al=[],"function"==typeof PerformanceObserver&&"function"==typeof PerformanceLongTaskTiming){var u=new PerformanceObserver((function(n){for(var r=n.getEntries(),e=0;e<r.length;e++)window.LUX_al.push(r[e])}));try{u.observe({type:"longtask"})}catch(n){}}return i}();`,
            }}
            strategy="afterInteractive"
          />
          <Script
            id="speedcurve-lux"
            src="https://cdn.speedcurve.com/js/lux.js?id=1861874380"
            strategy="afterInteractive"
            crossOrigin="anonymous"
            async
            defer
          />
          {/* 1% traffic hack to send RUM data to CoreWebVitals.io */}
          {Math.random() < rumSampleRate && (
            <Script
              strategy="afterInteractive"
              src="https://rum.corewebvitals.io/cwv/65ef6d545d9f9e7ae93a82be.js"
            />
          )}
        </>
      )}
      {/* END */}
    </>
  )
}
